import type { Container } from "pixi.js";
import { ReelAnticipation } from "./ReelAnticipation";
import type { ReelAnticipationFrame } from "./ReelAnticipationFrame";
import { ReelLandAnimations } from "./ReelLandAnimations";
import type { ReelMotionTuning } from "./ReelMotionTuning";
import { ReelRollAudio } from "./ReelRollAudio";
import { ReelDrum } from "./ReelDrum";
import type { GridAudioSoundPlayer, GridLayer, GridModel, PresentationTaskRunner, SpeedMode, SymbolDefinition, SymbolLandEffect, SymbolView } from "@slot/presentation";

export interface ReelSpinMechanicDeps {
  /** Optional only for headless tests: without a runner `spinToModel` paints the model instantly. */
  taskRunner?: PresentationTaskRunner | null;
  /** The reel roll loops and the landing one-shots the reactions play. */
  audio?: GridAudioSoundPlayer;
  motionTuning: ReelMotionTuning;
  /** Pre-blurred filler frames for the scrolling drum. */
  fillerBlurDefinitions?: Map<string, SymbolDefinition> | null;
  /**
   * The title's symbol ids the drum's random filler strip is padded with when fewer than three distinct symbols are
   * on screen (a uniform grid does not read as a spin). Non-empty: the mechanic never invents a symbol id.
   */
  fillerSymbolIds: readonly string[];
  speedModeProvider?: () => SpeedMode;
  /** The near-miss frame shown over an anticipated reel, and the overlay node it mounts in. */
  anticipationFrame?: { create: () => ReelAnticipationFrame; parent: Container } | null;
  anticipationSessionListener?: { onStart?(ordinal: number): void; onEnd?(): void } | null;
  landEffect?: SymbolLandEffect | null;
}

/**
 * The reel-spin mechanic over a `GridLayer`: a continuous drum scroll started on the player's
 * spin, the per-column stops onto the finals once the response arrives (with anticipation on
 * the server-flagged reels), the slam, and the landing hand-off to the grid. The drum itself is
 * the `ReelDrum`; the anticipation, the roll audio and the land animations are its
 * collaborators.
 */
export class ReelSpinMechanic {
  private readonly grid: GridLayer;
  private readonly taskRunner: PresentationTaskRunner | null;
  private readonly tuning: ReelMotionTuning;
  private readonly fillerBlurDefinitions: Map<string, SymbolDefinition> | null;
  private readonly fillerSymbolIds: readonly string[];
  private readonly speedMode: () => SpeedMode;
  private readonly roll: ReelRollAudio;
  private readonly anticipation: ReelAnticipation;
  private readonly landings: ReelLandAnimations;
  private drumInstance: ReelDrum | null = null;
  /** The in-flight continuous scroll (started before the response); null when none runs. */
  private continuousSpin: Promise<void> | null = null;
  /** The finals per column for the in-flight spin, for the per-column land sound before the cells bind. */
  private pendingFinalsByColumn: string[][] | null = null;

  public constructor(grid: GridLayer, deps: ReelSpinMechanicDeps) {
    this.grid = grid;
    this.taskRunner = deps.taskRunner ?? null;
    this.tuning = deps.motionTuning;
    this.fillerBlurDefinitions = deps.fillerBlurDefinitions ?? null;
    this.fillerSymbolIds = deps.fillerSymbolIds;
    this.speedMode = deps.speedModeProvider ?? (() => "normal");
    this.roll = new ReelRollAudio(deps.audio, deps.motionTuning.roll);
    this.anticipation = new ReelAnticipation({
      grid,
      taskRunner: this.taskRunner,
      roll: this.roll,
      frame: deps.anticipationFrame ?? null,
      listener: deps.anticipationSessionListener ?? null,
      scale: () => this.tuning.anticipation?.scale,
    });
    this.landings = new ReelLandAnimations(grid, deps.landEffect ?? null);
  }

  /** Lazily built so the grid's pool and layout exist before they are captured. */
  private get drum(): ReelDrum {
    if (!this.drumInstance) {
      this.drumInstance = new ReelDrum({ pool: this.grid.pool, layoutUtil: this.grid.layoutUtil, fillerBlurDefs: this.fillerBlurDefinitions });
    }
    return this.drumInstance;
  }

  /**
   * The player's slam: the running spin brakes every reel together — at once when the result is
   * known, else the moment it arrives. True when a spin was in flight.
   */
  public requestSlamStop(): boolean {
    return this.drumInstance?.requestSlamStop() ?? false;
  }

  /** Paints a model with no motion: any scroll or tease is torn down first; unchanged cells stay. */
  public renderModel(model: GridModel): void {
    this.clearAllTransitions();
    this.grid.renderGridModel(model, { preserveUnchanged: true });
  }

  /**
   * Stops the reels on `model`. The continuous scroll (started on the player's spin, or here for
   * a mid-round reveal) is handed the finals, brakes onto them honouring the minimum spin, and the
   * landed drum becomes the grid. Without a runner the model is painted instantly.
   */
  public async spinToModel(model: GridModel, anticipatedColumns?: number[]): Promise<void> {
    if (!this.taskRunner) {
      this.renderModel(model);
      return;
    }
    // A reveal never lands without spinning: a mid-round reveal (a free spin) starts its own scroll.
    if (!this.drumInstance?.isContinuousActive()) this.startContinuousSpin();
    const finalsByColumn = this.collectFinalColumnSymbols(model);
    this.pendingFinalsByColumn = finalsByColumn;
    this.anticipation.begin(anticipatedColumns);
    this.drum.provideContinuousFinals(finalsByColumn, anticipatedColumns);
    await this.continuousSpin;
    this.continuousSpin = null;
    // The landed drum is the grid before the render, which consumes the cell map: every landed
    // symbol as it is, still landing or not, and the render replaces only a cell that differs.
    this.landings.adoptLanded((column) => this.drum.getLandedColumnViews(column));
    this.renderModel(model);
  }

  /**
   * Starts the continuous scroll now, before the response: the drum scrolls random symbols until
   * `spinToModel` hands it the finals. Not awaited (the pre-spin would deadlock the "spin" barrier).
   */
  public startContinuousSpin(): void {
    const runner = this.taskRunner;
    if (!runner || this.drumInstance?.isContinuousActive()) return;
    // A fresh spin restarts the rising-pitch scatter arpeggio and closes the previous round's landings.
    this.grid.notifySequenceStart();
    this.landings.beginSpin();
    this.roll.startSpin();
    this.grid.ensureLayout();
    // The drum starts from what is on screen (no pop), then the static grid beneath it is released.
    const visibleViewsByColumn = this.captureVisibleColumnViews();
    this.drum.buildDrum({
      layout: this.grid.getComputedLayout(),
      gridColumnCount: this.grid.gridColumnCount,
      gridRowCount: this.grid.gridRowCount,
      candidateSymbolIds: this.collectVisibleCandidateSymbolIds(),
      visibleViewsByColumn,
    });
    this.releaseStaticGrid();
    this.continuousSpin = this.drum.spinAndForget({
      runner,
      layout: this.grid.getComputedLayout(),
      gridColumnCount: this.grid.gridColumnCount,
      gridRowCount: this.grid.gridRowCount,
      tuning: this.tuning,
      mode: this.speedMode(),
      onColumnLanded: (column) => {
        // The column's finals reach the land sound: the cells are not bound yet.
        this.grid.notifyColumnLanded(column, { symbolIds: this.pendingFinalsByColumn?.[column] });
        this.landings.playForColumn(column, this.drum.getLandedColumnViews(column));
        this.anticipation.columnLanded(column);
        if (column === this.grid.gridColumnCount - 1) this.roll.stopSpin();
      },
      onColumnAnticipateStart: (column) => this.anticipation.columnTeases(column),
    });
  }

  /**
   * Hands an overlay node (a dropping expanding wild) to the running drum so it scrolls out with
   * `column`: reparented into the grid root (clipped by the mask, above the symbols) at its current
   * spot, then driven by the column's scroll from there on (a hand-over after the spin began moves
   * on from where the node is). Resolves when it has scrolled clear or the spin ends; the caller
   * destroys the node then. slot-bloody-teddy hands its VS boards over at each spin start (plan §2.6).
   */
  public scrollOverlayOutWithColumn(column: number, node: Container): Promise<void> {
    const gridRoot = this.grid.gridRoot;
    if (!gridRoot || !node || node.destroyed || !this.drumInstance?.isContinuousActive()) return Promise.resolve();
    const local = node.parent ? gridRoot.toLocal(node.position, node.parent) : node.position.clone();
    gridRoot.addChild(node);
    node.position.copyFrom(local);
    return this.drum.attachColumnOverlay(column, node);
  }

  /** Aborts a running scroll without braking (a rejected spin, an error, a recovery: no finals will arrive). */
  public async abortContinuousSpin(): Promise<void> {
    if (!this.drumInstance?.isContinuousActive()) return;
    this.drumInstance.abortContinuous();
    await this.continuousSpin;
    this.continuousSpin = null;
  }

  /** Grid recovery: every landing back to rest, the drum released, the grid cleared. */
  public resetPresentationState(): void {
    this.landings.reset();
    this.drumInstance?.releaseDrum();
    this.clearAllTransitions();
    this.grid.resetPresentationState();
  }

  /**
   * The universal teardown every render path runs: a scroll still running with no finals (the
   * engine skipped the reveal on a rejection or an error) aborts first, then the drum is released
   * (except the views the grid owns), the tease and the rolls end.
   */
  private clearAllTransitions(): void {
    this.drumInstance?.abortContinuous();
    this.continuousSpin = null;
    this.drumInstance?.releaseDrum((view) => this.landings.owns(view));
    this.anticipation.reset();
    this.roll.stopAll();
  }

  /** The model transposed to per-column, top-to-bottom symbol ids: the shape each reel's resting lanes take. */
  private collectFinalColumnSymbols(model: GridModel): string[][] {
    const byColumn: string[][] = [];
    for (let col = 0; col < this.grid.gridColumnCount; col++) {
      const column: string[] = [];
      for (let row = 0; row < this.grid.gridRowCount; row++) column.push(model.cells[row]?.[col]?.symbolId ?? "");
      byColumn.push(column);
    }
    return byColumn;
  }

  /** The distinct symbols on screen, the random strip the drum cycles; padded with the title's filler ids when too uniform to read as a spin. */
  private collectVisibleCandidateSymbolIds(): string[] {
    const ids = new Set<string>();
    for (const view of this.grid.views()) {
      const id = view?.getSymbolId();
      if (id) ids.add(id);
    }
    if (ids.size < 3) for (const id of this.fillerSymbolIds) ids.add(id);
    return [...ids];
  }

  /** The displayed views per column, top to bottom (null for an unbound cell): the drum's seed. */
  private captureVisibleColumnViews(): (SymbolView | null)[][] {
    const byColumn: (SymbolView | null)[][] = [];
    for (let col = 0; col < this.grid.gridColumnCount; col++) {
      const column: (SymbolView | null)[] = [];
      for (let row = 0; row < this.grid.gridRowCount; row++) column.push(this.grid.cellAt(row * this.grid.gridColumnCount + col) ?? null);
      byColumn.push(column);
    }
    return byColumn;
  }

  /** Releases the static grid once the drum covers it; views the drum adopted as lanes and dirty views stay. */
  private releaseStaticGrid(): void {
    for (const view of new Set(this.grid.views())) {
      if (view && (this.drum.wasAdoptedAsLane(view) || !view.isPoolReusable())) continue;
      try {
        this.grid.releaseView(view, "reel_spin_takeover");
      } catch {
        // Already released.
      }
    }
    this.grid.forgetAll();
  }
}
